import { Component, type ErrorInfo, type ReactNode } from "react";

/**
 * Confines a render crash to one region instead of unmounting the whole app.
 *
 * Why this exists, from CLAUDE.md rule 23f: the admin SPA and the API deploy in
 * PARALLEL off the same merge, and the admin lands first -- `deploy-admin.yml`
 * is an S3 sync while `deploy-aws.yml` is a Docker build plus CloudFormation
 * plus alembic. For those minutes the new bundle reads fields the old API does
 * not send, and a non-defensive read is fatal rather than degraded:
 * `r.orders.toLocaleString()` on `undefined` throws mid-render. With no boundary
 * anywhere in `admin/src`, React unmounted the entire tree -- a blank dashboard
 * that a reload does not fix until the API catches up. Observed on PR #209's
 * merge (2026-08-25), not theorised.
 *
 * The per-field `?? 0` fallbacks in `ResponseBreakdownRow.tsx` and `utils/csv.ts`
 * are point fixes for the two reads that were known to be exposed. This is the
 * structural half: it does not require anyone to have predicted WHICH field
 * would be missing.
 *
 * ## What it does NOT catch
 *
 * React error boundaries catch throws during render, in lifecycle methods, and
 * in constructors of the tree below. They do NOT catch:
 *   - rejected promises / `fetch` failures (the pages already handle those
 *     explicitly, and `Analytics` has a dedicated timeout message),
 *   - throws inside event handlers,
 *   - anything in an `useEffect` callback that runs after paint.
 * So this is a floor, not a substitute for handling the failure where it
 * happens. Do not delete a `?? 0` because a boundary now exists: degrading one
 * cell is better than blanking one panel.
 */

type Props = {
  /**
   * What failed, named for an operator rather than a developer ("Response
   * breakdowns", not "PagesTable"). It goes in the fallback and in the console
   * line, which is the only telemetry this app has -- there is no error
   * reporting service wired up.
   */
  label: string;
  /**
   * Changing this clears a caught error and retries the children.
   *
   * Load-bearing, not a convenience: a boundary latches by design, so without
   * it one bad payload wedges the panel until a full page reload -- including
   * after the API deploy that caused it has finished rolling out.
   *
   * Pass something that changes when the DATA ARRIVES, not when the user
   * changes a filter. This ordering is easy to get wrong and it was, once: a
   * key built from the filter values changes at CLICK time, before the refetch
   * resolves, so the boundary clears, re-renders against the stale broken
   * payload, throws, and re-latches -- and the good response then arrives with
   * the key unchanged, leaving the panel dead for exactly the reason the reset
   * existed to prevent. `Analytics` passes a counter it bumps on every
   * successful response; `Layout`, which renders no fetched data, passes the
   * pathname.
   */
  resetKey?: string | number;
  children: ReactNode;
};

type State = { error: Error | null };

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error(
      `[admin] ${this.props.label} failed to render`,
      error,
      info.componentStack,
    );
  }

  override componentDidUpdate(prev: Props): void {
    if (this.state.error !== null && prev.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  private retry = (): void => {
    this.setState({ error: null });
  };

  override render(): ReactNode {
    const { error } = this.state;
    if (error === null) return this.props.children;

    return (
      <div
        role="alert"
        data-testid="error-boundary-fallback"
        className="rounded-2xl border border-red-900/60 bg-red-950/40 p-6 text-sm text-red-200"
      >
        <div className="mono text-[0.65rem] font-bold uppercase tracking-[0.18em] text-red-300/80">
          {this.props.label} unavailable
        </div>
        <p className="mt-2">
          This panel failed to render. The rest of the page is unaffected. If
          the API was just deployed, wait a minute and retry — the admin app
          ships ahead of the API and briefly reads fields it cannot serve yet.
        </p>
        {/* The message, not the stack: an operator pasting this into Slack is
            the fastest diagnosis path this app has, and a stack is unreadable
            there. The full stack plus component trace goes to the console. */}
        <p className="mono mt-3 break-words text-[0.7rem] text-red-300/70">
          {error.message || String(error)}
        </p>
        <button
          type="button"
          onClick={this.retry}
          className="mono mt-4 rounded-md border border-red-800 px-3 py-1.5 text-[0.65rem] uppercase tracking-[0.18em] text-red-200 transition-colors hover:border-red-500 hover:text-red-100"
        >
          Retry
        </button>
      </div>
    );
  }
}
